import React, { useEffect, useState } from 'react';
import BRAND from '../brand';
import { api, getPlatformUser, fetchWebUser } from '../api';
import { getPlatform, openLink, closeApp, getStartParam } from '../platform';
import SmsAuthInline from '../components/SmsAuthInline';
import { EmptyStateIcon, StatusIcon, FieldIcon, SectionLoader , HeroIcon } from '../components/NavIcons';
import CertificatePurchasePage from '../components/CertificatePurchasePage';
import CertificateActivateForm from '../components/CertificateActivateForm';
import CertificatesList from '../components/CertificatesList';
import {
  trackProfileOpened,
  trackClickTelegram,
  trackClickMax,
  trackMergeButtonClicked,
  trackMergeDeeplinkToTg,
  trackMergeDeeplinkToMax,
} from '../utils/metrika';
import { maxBotUrl, tgBotUrl } from '../utils/attribution';

function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const d = String(phone).replace(/\D/g, '').slice(-10);
  if (d.length !== 10) return phone;
  return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
}

function ProfilePage({ goToOrders }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [linkState, setLinkState] = useState('idle'); // idle, loading, ready, error
  const [linkData, setLinkData] = useState(null); // { link, token, expires_in_minutes }
  const [linkError, setLinkError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showMaxSupportPopup, setShowMaxSupportPopup] = useState(false);
  const [webAuthOpen, setWebAuthOpen] = useState(false);
  const [jwt, setJwt] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem('stems_jwt') : null));
  const [showCertPurchase, setShowCertPurchase] = useState(false);
  const [certRefreshKey, setCertRefreshKey] = useState(0);
  const [activateCodeFromUrl, setActivateCodeFromUrl] = useState(() => {
    if (typeof window === 'undefined') return '';
    // Код, который уже активировали в этой сессии, подставлять нельзя: он
    // остаётся в start_param, и профиль предлагал активировать его снова.
    let used = '';
    try { used = (localStorage.getItem('stems_cert_activated_code') || '').toUpperCase(); } catch (e) {}
    try {
      // 1) Старый URL query (web_app fallback / web версия)
      const params = new URLSearchParams(window.location.search);
      const fromQuery = (params.get('activate') || '').toUpperCase();
      if (fromQuery) return fromQuery === used ? '' : fromQuery;
      // 2) start_param из direct mini app link `cert_<CODE>`
      // Та же логика что в App.jsx getInitialPage: при reload вебвью
      // не подставляем код повторно, чтобы юзер мог обновить mini-app
      // и попасть на чистый профиль.
      const sp = getStartParam();
      if (sp && sp.startsWith('cert_')) {
        const navType = performance.getEntriesByType('navigation')[0]?.type;
        if (navType === 'reload' || navType === 'back_forward') return '';
        const fromStart = sp.slice(5).toUpperCase();
        return fromStart === used ? '' : fromStart;
      }
    } catch {}
    return '';
  });

  const platformUser = getPlatformUser();
  const platform = getPlatform();
  const isWebGuest = platform === 'web' && !jwt;
  const isWebAuth = platform === 'web' && !!jwt;

  useEffect(() => {
    trackProfileOpened();
    if (isWebGuest) {
      setIsLoading(false);
      return;
    }
    if (isWebAuth) {
      loadWebUser();
      return;
    }
    loadUserData();
    // Одноразовая загрузка профиля при маунте
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Предзагрузка ссылки привязки (только для TG/Max — не для web)
  useEffect(() => {
    if (platform === 'web') return;
    if (!user || (user.telegram_id && user.max_id)) return;
    const platformId = platformUser?.telegram_id || platformUser?.max_id;
    if (!platformId) return;
    api.createLinkToken(platformId)
      .then(result => { setLinkData(result); })
      .catch(() => {});
    // platform/platformUser стабильны в рамках сессии, реагируем только на user
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadUserData = async () => {
    if (!platformUser) {
      setIsLoading(false);
      return;
    }
    try {
      const userData = await api.getOrCreateUser(platformUser);
      setUser(userData);
    } catch (error) {
      console.log('Error loading user data:', error);
      setUser({
        first_name: platformUser.first_name,
        last_name: platformUser.last_name,
        username: platformUser.username,
        bonus_balance: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadWebUser = async () => {
    try {
      const data = await fetchWebUser();
      if (!data) {
        // JWT недействителен — сбрасываем
        localStorage.removeItem('stems_jwt');
        setJwt(null);
      } else {
        setUser(data);
      }
    } catch (error) {
      console.log('Error loading web user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('stems_jwt');
    localStorage.removeItem('stems_checkout_form');
    localStorage.removeItem('stems_sub_checkout_form');
    setJwt(null);
    setUser(null);
  };

  // Polling: после показа ссылки проверяем произошла ли привязка
  useEffect(() => {
    if (linkState !== 'ready') return;
    const interval = setInterval(async () => {
      try {
        const userData = await api.getOrCreateUser(platformUser);
        if (userData.telegram_id && userData.max_id) {
          clearInterval(interval);
          setUser(userData);
          setLinkState('idle');
          setLinkData(null);
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
    // platformUser стабилен в рамках сессии
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkState]);

  const handleCreateLink = () => {
    const otherPlatform = platform === 'max' ? 'telegram' : 'max';
    trackMergeButtonClicked(otherPlatform);
    // Ссылка предзагружена — открываем браузер по прямому клику и показываем UI
    if (linkData?.link) {
      if (otherPlatform === 'telegram') trackMergeDeeplinkToTg();
      else trackMergeDeeplinkToMax();
      // Max Desktop обрезает query params у t.me ссылок — пропускаем через редирект
      const url = (platform === 'max' && linkData.link.startsWith('https://t.me/'))
        ? `${window.location.origin}/api/r?url=${encodeURIComponent(linkData.link)}`
        : linkData.link;
      openLink(url);
      setLinkState('ready');
      return;
    }
    // Ссылка не загрузилась — генерируем и показываем для ручного копирования
    const platformId = platformUser.telegram_id || platformUser.max_id;
    if (!platformId) return;
    setLinkState('loading');
    setLinkError(null);
    api.createLinkToken(platformId)
      .then(result => { setLinkData(result); setLinkState('ready'); })
      .catch(error => { setLinkError(error.message); setLinkState('error'); });
  };

  const handleCopyLink = async () => {
    if (!linkData?.link) return;
    try {
      await navigator.clipboard.writeText(linkData.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback для старых браузеров
      const ta = document.createElement('textarea');
      ta.value = linkData.link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // ─── Web-гость: показываем авторизацию ───
  if (isWebGuest) {
    return (
      <div className="profile-page">
        <div style={{
          // Фирменная мягкая подложка вместо чужого зелёного (цвета/шрифт — из бренд-пака)
          background: 'var(--primary-soft-bg)',
          borderRadius: '10px',
          padding: '14px 16px',
          margin: '12px 20px 0',
          fontSize: '14px',
          color: 'var(--text-color)',
          textAlign: 'center',
        }}>
          <div style={{ marginBottom: '10px' }}>
            После авторизации вы сможете отслеживать статус выполнения и историю заказов, пополнять и списывать счёт {BRAND.name}.
          </div>

          {webAuthOpen ? (
            <div style={{ marginBottom: '12px', maxWidth: '300px', margin: '0 auto 12px' }}>
              <SmsAuthInline
                onSuccess={async () => {
                  const fresh = await fetchWebUser();
                  if (fresh) setUser(fresh);
                  setJwt(localStorage.getItem('stems_jwt'));
                  setWebAuthOpen(false);
                }}
                onCancel={() => setWebAuthOpen(false)}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setWebAuthOpen(true)}
              style={{
                background: 'var(--primary-color)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: 'var(--on-primary)',
                marginBottom: '10px',
              }}
            >
              Авторизация через SMS
            </button>
          )}

          <div style={{ fontSize: '12px', color: '#555', margin: '4px 0 8px' }}>
            или откройте приложение в мессенджере
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <a
              href={tgBotUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClickTelegram('profile_auth')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: '#2AABEE', color: 'white', textDecoration: 'none',
                borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.492-1.302.48-.428-.012-1.252-.242-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              Telegram
            </a>
            <a
              href={maxBotUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClickMax('profile_auth')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: 'linear-gradient(135deg, #00C8FF 0%, #5B6FFF 50%, #A855F7 100%)',
                color: 'white', textDecoration: 'none',
                borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12c0 1.77.46 3.43 1.27 4.88L2 22l5.12-1.24A9.96 9.96 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 14.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9z" fill="white"/></svg>
              Max
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ─── Для мини-приложений — требуется platformUser ───
  if (!platformUser && !isWebAuth) {
    return (
      <div className="profile-page" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ marginBottom: '16px' }}><HeroIcon name="lock" size={56} /></div>
        <h2 style={{ marginBottom: '8px', fontSize: '20px' }}>Доступно только в Telegram или Max</h2>
        <p style={{ color: '#666', fontSize: '14px' }}>
          Для просмотра профиля откройте приложение через бот
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="profile-page">
        <SectionLoader />
      </div>
    );
  }

  // Full-page оформление покупки сертификата (вместо модалки)
  if (showCertPurchase) {
    return (
      <CertificatePurchasePage
        onBack={() => setShowCertPurchase(false)}
        onPurchased={() => setCertRefreshKey(k => k + 1)}
        goToOrders={goToOrders}
      />
    );
  }

  // Имя зависит от платформы:
  // - TG: tg_first_name → first_name (legacy)
  // - Max: max_first_name → first_name (legacy)
  // - Web: first_name (= имя из web-заказа для pure-web, либо имя при создании
  //   мессенджер-юзера) → fallback на tg_first_name → max_first_name
  let userName;
  let userLastName;
  if (platform === 'telegram') {
    userName = user?.tg_first_name || user?.first_name || '';
    userLastName = user?.tg_last_name || user?.last_name || '';
  } else if (platform === 'max') {
    userName = user?.max_first_name || user?.first_name || '';
    userLastName = user?.max_last_name || user?.last_name || '';
  } else {
    // Web: приоритет — имя из web-заказа (web_first_name), затем fallback на TG/Max
    userName = user?.web_first_name || user?.tg_first_name || user?.max_first_name || user?.first_name || '';
    userLastName = '';  // фамилия в web не используется
  }
  const userInitial = userName ? userName.charAt(0).toUpperCase() : '';
  const bonusBalance = user?.bonus_balance || 0;

  // Аккаунты уже объединены?
  const isLinked = user?.telegram_id && user?.max_id;

  // Текст кнопки зависит от платформы
  const otherPlatformName = platform === 'telegram' ? 'Max' : 'Telegram';

  // ─── Web-авторизованный юзер ───
  if (isWebAuth) {
    const hasTelegram = !!user?.telegram_id;
    const hasMax = !!user?.max_id;

    const handleLinkPlatform = async (targetPlatform) => {
      try {
        const result = await api.createLinkTokenWeb(targetPlatform);
        if (result?.link) {
          // Открываем ссылку в новой вкладке: пользователь зайдёт в бот,
          // оттуда Mini App подхватит start_param=link_TOKEN и сам вызовет /link/confirm
          window.open(result.link, '_blank', 'noopener,noreferrer');
        }
      } catch (e) {
        alert(e.message || 'Не удалось создать ссылку');
      }
    };

    return (
      <div className="profile-page">
        {/* Header — компактный: avatar + имя + телефон тонко */}
        <div className="profile-header">
          <div className="profile-avatar">{userInitial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="profile-name" style={{ margin: 0 }}>{userName} {userLastName}</h2>
            {user?.phone && (
              <div style={{ color: '#999', fontSize: 13, marginTop: 2 }}>{formatPhoneDisplay(user.phone)}</div>
            )}
          </div>
        </div>

        {/* Финансовый блок — баланс (цвет акцента бренда) + сертификаты (белый).
            Текст поверх акцента — только через --on-primary: у Стемса это тот же тёмный
            #1A1A1A, у Букетберри кремовый на тёмно-зелёном (раньше был чёрный на тёмном). */}
        <div style={{
          margin: '16px 0',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <div style={{
            background: 'var(--primary-color)',
            color: 'var(--on-primary)',
            padding: '20px 20px 24px',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: 12, fontWeight: 600, letterSpacing: 1.5,
              textTransform: 'uppercase', opacity: 0.6,
              marginBottom: 6,
            }}>
              Счёт {BRAND.name}
            </div>
            <div style={{
              fontSize: 'clamp(36px, 10vw, 44px)', fontWeight: 800,
              lineHeight: 1,
            }}>
              {bonusBalance.toLocaleString('ru-RU')} <span style={{ fontWeight: 600, fontSize: '0.6em' }}>₽</span>
            </div>
            <div style={{
              fontSize: 12, opacity: 0.6, marginTop: 8, lineHeight: 1.4,
            }}>
              За каждый заказ начисляется кешбэк
            </div>
          </div>

          <div style={{ background: '#fff', padding: '16px 20px' }}>
            <CertificateActivateForm
              inline
              defaultCode={activateCodeFromUrl}
              onActivated={() => { setCertRefreshKey(k => k + 1); loadWebUser(); }}
            />
            <CertificatesList refreshKey={certRefreshKey} />
          </div>
        </div>

        {/* Системная секция — iOS Settings: телефон, IDs, привязки */}
        <div style={{
          margin: '16px 0',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          {user?.phone && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px',
              borderBottom: '1px solid #F0F0F0',
            }}>
              <span style={{ fontSize: 14, color: '#666' }}>Телефон</span>
              <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
                {formatPhoneDisplay(user.phone)}
              </span>
            </div>
          )}

          {hasTelegram && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px',
              borderBottom: '1px solid #F0F0F0',
            }}>
              <span style={{ fontSize: 14, color: '#666' }}>Telegram ID</span>
              <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>{user.telegram_id}</span>
            </div>
          )}

          {hasMax && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px',
              borderBottom: '1px solid #F0F0F0',
            }}>
              <span style={{ fontSize: 14, color: '#666' }}>Max ID</span>
              <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>{user.max_id}</span>
            </div>
          )}

          {!hasTelegram && (
            <button
              type="button"
              onClick={() => handleLinkPlatform('telegram')}
              style={{
                width: '100%',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 20px',
                border: 'none',
                borderBottom: '1px solid #F0F0F0',
                background: '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 14,
              }}
            >
              <span style={{ color: '#333' }}>
                <HeroIcon name="link" size={18} color="currentColor" inline /><span style={{ marginRight: 8 }} />
                Привязать Telegram
              </span>
              <span style={{ color: '#BBB', fontSize: 18 }}>›</span>
            </button>
          )}

          {!hasMax && (
            <button
              type="button"
              onClick={() => handleLinkPlatform('max')}
              style={{
                width: '100%',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 20px',
                border: 'none',
                borderBottom: (hasTelegram && hasMax) ? 'none' : '1px solid #F0F0F0',
                background: '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 14,
              }}
            >
              <span style={{ color: '#333' }}>
                <HeroIcon name="link" size={18} color="currentColor" inline /><span style={{ marginRight: 8 }} />
                Привязать Max
              </span>
              <span style={{ color: '#BBB', fontSize: 18 }}>›</span>
            </button>
          )}

          {hasTelegram && hasMax && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '14px 20px',
              fontSize: 14, color: '#4CAF50',
            }}>
              <span>✓</span>
              <span>Аккаунты Telegram и Max объединены</span>
            </div>
          )}
        </div>

        {/* Поддержка — тонкая текст-ссылка в футере */}
        <div style={{ textAlign: 'center', padding: '24px 16px 4px' }}>
          <button
            type="button"
            onClick={() => setShowMaxSupportPopup(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 16px',
              color: '#888',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 4,
            }}
          >
            <StatusIcon name="chat" size={14} />Нужна помощь? Связаться с поддержкой
          </button>
        </div>

        {/* Выход — мелкая ссылка под поддержкой */}
        <div style={{ textAlign: 'center', padding: '4px 16px 12px' }}>
          <button
            onClick={handleLogout}
            style={{
              background: 'none', border: 'none', color: '#BBB',
              fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
              textUnderlineOffset: 4, padding: '4px 8px',
            }}
          >
            Выйти из аккаунта
          </button>
        </div>

        {/* Попап поддержки */}
        {showMaxSupportPopup && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
          }} onClick={() => setShowMaxSupportPopup(false)}>
            <div style={{
              background: 'white', borderRadius: 16, padding: '20px 24px',
              maxWidth: 320, width: '100%', textAlign: 'center',
            }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: 'var(--text-secondary)' }}><FieldIcon name="chat" size={28} /></div>
              <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#333' }}>Поддержка</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                Сформулируйте свой вопрос, а также укажите номер телефона и номер заказа, если он у вас есть — мы обязательно поможем.
              </p>
              {platform === 'max' ? (
                <button
                  onClick={() => { setShowMaxSupportPopup(false); closeApp(); }}
                  style={{
                    width: '100%', padding: 14, background: '#4CAF50', color: 'white',
                    border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                    marginBottom: 8,
                  }}
                >Открыть чат</button>
              ) : BRAND.supportUrl ? (
                <a
                  href={BRAND.supportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowMaxSupportPopup(false)}
                  style={{
                    display: 'block', textDecoration: 'none', textAlign: 'center',
                    width: '100%', padding: 14, background: '#4CAF50', color: 'white',
                    borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                    marginBottom: 8, boxSizing: 'border-box',
                  }}
                >Открыть чат</a>
              ) : null}
              <button
                onClick={() => setShowMaxSupportPopup(false)}
                style={{
                  width: '100%', padding: 12, background: 'none', color: '#999',
                  border: 'none', fontSize: 14, cursor: 'pointer',
                }}
              >Закрыть</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Header — компактный: avatar + имя, username тонко справа */}
      <div className="profile-header">
        <div className="profile-avatar">{userInitial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="profile-name" style={{ margin: 0 }}>{userName} {userLastName}</h2>
          {user?.username && (
            <div style={{ color: '#999', fontSize: 13, marginTop: 2 }}>@{user.username}</div>
          )}
        </div>
      </div>

      {/* Главный финансовый блок — баланс (бренд-жёлтый) + действия (белый) */}
      <div style={{
        margin: '16px 0',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        {/* Зона с балансом в цвете акцента бренда; текст — через --on-primary (см. блок выше) */}
        <div style={{
          background: 'var(--primary-color)',
          color: 'var(--on-primary)',
          padding: '20px 20px 24px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: 12, fontWeight: 600, letterSpacing: 1.5,
            textTransform: 'uppercase', opacity: 0.6,
            marginBottom: 6,
          }}>
            Счёт {BRAND.name}
          </div>
          <div style={{
            fontSize: 'clamp(36px, 10vw, 44px)', fontWeight: 800,
            lineHeight: 1,
          }}>
            {bonusBalance.toLocaleString('ru-RU')} <span style={{ fontWeight: 600, fontSize: '0.6em' }}>₽</span>
          </div>
          <div style={{
            fontSize: 12, opacity: 0.6, marginTop: 8, lineHeight: 1.4,
          }}>
            За каждый заказ начисляется кешбэк
          </div>
        </div>

        {/* Белая зона с действиями по сертификатам */}
        <div style={{ background: '#fff', padding: '16px 20px' }}>
          {platform !== 'web' && (
            <button
              type="button"
              onClick={() => {
                try { window.scrollTo(0, 0); } catch {}
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
                setShowCertPurchase(true);
              }}
              className="primary-btn"
              style={{
                // Фирменная жёлтая, как «Перейти в каталог»: белая версия
                // терялась на белом фоне страницы
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                marginBottom: 14,
              }}
            >
              <span style={{ display: 'inline-flex', verticalAlign: '-3px', marginRight: 8 }}>
                <EmptyStateIcon name="gift" size={17} color="currentColor" />
              </span>
              Оформить подарочный сертификат
            </button>
          )}

          <CertificateActivateForm
            inline
            defaultCode={activateCodeFromUrl}
            onActivated={() => { setCertRefreshKey(k => k + 1); loadUserData(); }}
          />
          <CertificatesList refreshKey={certRefreshKey} />
        </div>
      </div>

      {/* Системная секция — компактный список (стиль iOS Settings) */}
      <div style={{
        margin: '16px 0',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}>
        {/* ID платформы */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 20px',
          borderBottom: '1px solid #F0F0F0',
        }}>
          <span style={{ fontSize: 14, color: '#666' }}>
            {platform === 'telegram' ? 'Telegram ID' : 'Max ID'}
          </span>
          <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
            {platform === 'telegram' ? user?.telegram_id : user?.max_id}
          </span>
        </div>

        {isLinked && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 20px',
            borderBottom: '1px solid #F0F0F0',
          }}>
            <span style={{ fontSize: 14, color: '#666' }}>
              {platform === 'telegram' ? 'Max ID' : 'Telegram ID'}
            </span>
            <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
              {platform === 'telegram' ? user?.max_id : user?.telegram_id}
            </span>
          </div>
        )}

        {user?.phone && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 20px',
            borderBottom: '1px solid #F0F0F0',
          }}>
            <span style={{ fontSize: 14, color: '#666' }}>Телефон</span>
            <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
              {formatPhoneDisplay(user.phone)}
            </span>
          </div>
        )}

        {/* Объединение аккаунтов — компактная action-строка */}
        {!isLinked && linkState === 'idle' && (
          <button
            type="button"
            onClick={handleCreateLink}
            style={{
              width: '100%',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px',
              border: 'none',
              borderBottom: '1px solid #F0F0F0',
              background: '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 14,
            }}
          >
            <span style={{ color: '#333' }}>
              <HeroIcon name="link" size={18} color="currentColor" inline /><span style={{ marginRight: 8 }} />
              Объединить с {otherPlatformName}
            </span>
            <span style={{ color: '#BBB', fontSize: 18 }}>›</span>
          </button>
        )}

        {isLinked && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '14px 20px',
            borderBottom: '1px solid #F0F0F0',
            fontSize: 14, color: '#4CAF50',
          }}>
            <span>✓</span>
            <span>Аккаунты Telegram и Max объединены</span>
          </div>
        )}

        {/* Раскрывающаяся секция привязки */}
        {!isLinked && linkState !== 'idle' && (
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F0F0F0' }}>
            {linkState === 'loading' && (
              <div style={{ color: '#666', fontSize: 14, textAlign: 'center' }}>Создаём ссылку...</div>
            )}
            {linkState === 'ready' && linkData && (
              <>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#666', lineHeight: 1.4 }}>
                  Откройте ссылку, затем — приложение {otherPlatformName}. Действительна {linkData.expires_in_minutes} мин.
                  {platform === 'telegram' && <><br/><strong style={{ color: '#333' }}>Важно:</strong> закройте мини-приложение в {otherPlatformName} перед переходом.</>}
                </p>
                <div style={{
                  background: '#F5F5F5', borderRadius: 8, padding: 10,
                  fontSize: 12, color: '#555', wordBreak: 'break-all', marginBottom: 10,
                }}>
                  {linkData.link}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleCopyLink}
                    style={{
                      flex: 1, padding: 10, border: 'none', borderRadius: 8,
                      background: copied ? '#4CAF50' : 'var(--text-color)',
                      color: copied ? '#fff' : 'var(--primary-color)',
                      fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}
                  >{copied ? 'Скопировано!' : 'Скопировать'}</button>
                  <button
                    onClick={() => { setLinkState('idle'); setLinkData(null); }}
                    style={{
                      padding: 10, border: '1px solid #DDD', borderRadius: 8,
                      background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer',
                    }}
                  >Отмена</button>
                </div>
              </>
            )}
            {linkState === 'error' && (
              <div>
                <div style={{ color: '#f44336', fontSize: 13, marginBottom: 10 }}>
                  {linkError || 'Ошибка при создании ссылки'}
                </div>
                <button
                  onClick={handleCreateLink}
                  style={{
                    width: '100%', padding: 10, border: 'none', borderRadius: 8,
                    background: 'var(--text-color)', color: 'var(--primary-color)',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >Попробовать ещё раз</button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Поддержка — тонкая текст-ссылка в футере, отделена от системной секции */}
      <div style={{ textAlign: 'center', padding: '24px 16px 12px' }}>
        <button
          type="button"
          onClick={() => setShowMaxSupportPopup(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px 16px',
            color: '#888',
            fontSize: 13,
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: 4,
          }}
        >
          <StatusIcon name="chat" size={14} />Нужна помощь? Связаться с поддержкой
        </button>
      </div>

      {/* Попап поддержки для Max */}
      {showMaxSupportPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
        }} onClick={() => setShowMaxSupportPopup(false)}>
          <div style={{
            background: 'white', borderRadius: 16, padding: '20px 24px',
            maxWidth: 320, width: '100%', textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: 'var(--text-secondary)' }}><FieldIcon name="chat" size={28} /></div>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#333' }}>Поддержка</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666', lineHeight: 1.5 }}>
              Сформулируйте свой вопрос, а также укажите номер телефона и номер заказа, если он у вас есть — мы обязательно поможем.
            </p>
            {platform === 'max' ? (
              <button
                onClick={() => { setShowMaxSupportPopup(false); closeApp(); }}
                style={{
                  width: '100%', padding: 14, background: '#4CAF50', color: 'white',
                  border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                  marginBottom: 8,
                }}
              >Открыть чат</button>
            ) : BRAND.supportUrl ? (
              <a
                href={BRAND.supportUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowMaxSupportPopup(false)}
                style={{
                  display: 'block', textDecoration: 'none', textAlign: 'center',
                  width: '100%', padding: 14, background: '#4CAF50', color: 'white',
                  borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                  marginBottom: 8, boxSizing: 'border-box',
                }}
              >Открыть чат</a>
            ) : null}
            <button
              onClick={() => setShowMaxSupportPopup(false)}
              style={{
                width: '100%', padding: 12, background: 'none', color: '#999',
                border: 'none', fontSize: 14, cursor: 'pointer',
              }}
            >Закрыть</button>
          </div>
        </div>
      )}

    </div>
  );
}

export default ProfilePage;
